import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ApprovalRequestUser } from 'common/models/approval-requests';
import { ApprovalRequestsState } from 'private/app/store/approval-requests.state';
import { Observable } from 'rxjs';
import { UserRegistrationsForm, UserRegistrationsService } from '../../services/account-admin/user/user-registrations.service';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup } from '@angular/forms';
import { FetchApprovalRequests } from 'private/app/store/approval-requests.actions';
import { Option } from 'common/components/select/select.component';
import { UserApiService } from 'private/app/services/account-admin/user/user-api.service';
import { CreateUserDetails, RegisterUsers, UserDetails, UserRegistration, UserRegistrationDetails, UserRegistrationRequest } from 'private/app/models/user.model';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';
import { Router } from '@angular/router';
import { ToastService, ToastTheme } from 'common/services/toast.service';
import { UserRegistrationsResultsComponent } from './user-registrations-results/user-registrations-results.component';
import { AccountAdminExtendedService } from 'private/app/services/account-admin/account-admin-extended.service';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { map } from 'rxjs/operators';

@Component({
    selector: 'hvac-user-registrations',
    templateUrl: './user-registrations.component.html',
    styleUrls: ['./user-registrations.component.scss']
})
export class UserRegistrationsComponent implements OnInit, OnDestroy {
    @Select(ApprovalRequestsState.approvalRequestUsers) approvalRequestUsers$: Observable<ApprovalRequestUser[]>;
    @ViewChild(UserRegistrationsResultsComponent) userRegistrationsResults!: UserRegistrationsResultsComponent;
    public userRegistrationsSearchForm: FormGroup<UserRegistrationsForm>;
    public userRegistrationStepNumber: string;
    public usersData : UserRegistrationRequest;
    public userRegistrationDetails: UserRegistrationDetails;
    public userDetails: UserDetails;
    public userRegistrationRequest: UserRegistrationRequest[] = [];
    public originaluserRegistrationRequest: UserRegistrationRequest[] = [];
    public loading: boolean = false;
    public companyTypes: Option[] = [];
    public countryList$: Observable<Option[]>;

    public breadCrumbData = {
        Content: { title: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.HEADING') },
        breadCrumbs: [
            {
                title: this.translateService.instant('ACCOUNT_ADMIN.ADMIN'),
                url: '/admin'
            },
            {
                title: this.translateService.instant('ACCOUNT_ADMIN.ADMIN_TOOLS'),
                url: '/admin/admin-tools'
            },
            {
                title: this.translateService.instant('ACCOUNT_ADMIN.ACCOUNT_ADMIN'),
                url: '/Admin/Admin-Tools/account-admin.html'
            }
        ]
    };

    public userRegistrationStatusOptions: Option[];
    public originalApprovalRequestUsers: ApprovalRequestUser[] = [];
    public filteredApprovalRequestUsers: ApprovalRequestUser[] = [];
    public activeScreenName: string;
    public apiRequestActive = false;

    constructor(
        public translateService: TranslateService,
        public userAPIService : UserApiService,
        public readonly accountAdminExtendedService: AccountAdminExtendedService,
        public userAdminService: UserAdminService,
        public commonUtils: CommonUtilsService,
        private router: Router,
        private userRegistrationService: UserRegistrationsService,
        private readonly store: Store,
        private readonly toastService: ToastService
    ) {}

    @HostListener('document:keydown.enter', ['$event'])
    onEnterPress() {
        this.search();
    }

    ngOnInit(): void {
        this.userAdminService.smoothScrollToTop();
        this.userRegistrationsSearchForm = this.userRegistrationService.userRegistrationsSearchForm;
        this.accountAdminExtendedService.getCompanyTypes().subscribe((data) => {
            this.companyTypes = data.map(({ companyTypes }) => companyTypes).flat().map((companyType) => ({
                name: companyType?.name!,
                value: companyType?.code!
            })).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            this.companyTypes.unshift({
                name: this.translateService.instant('ACCOUNT_ADMIN.SELECT_COMPANY_TYPE'),
                value: ''
            });
        });
        this.countryList$ = this.commonUtils.getCountryOptions().pipe(
            map((countries) => this.commonUtils.reprioritizeCountries(countries))
        );
        this.store.dispatch(new FetchApprovalRequests());
        this.userRegistrationStepNumber = 'step0_searchUser';
        this.loading = true;
        this.userAPIService.userRegistrationRequest({}).subscribe((requests) => {
            if (requests) {
                this.loading = false;
                this.userRegistrationRequest = requests.registrations;
                this.originaluserRegistrationRequest = requests.registrations;
            }
        });
        this.approvalRequestUsers$.subscribe((data: ApprovalRequestUser[]) => {
            this.originalApprovalRequestUsers = [...data];
            this.filteredApprovalRequestUsers = data;
        });
        this.userRegistrationStatusOptions = [
            {
                name: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.DROPDOWN_OPTIONS.STATUS.SELECT'),
                value: ''
            },
            {
                name: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.DROPDOWN_OPTIONS.STATUS.PENDING'),
                value: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.DROPDOWN_OPTIONS.STATUS.PENDING')
            },
            {
                name: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.DROPDOWN_OPTIONS.STATUS.FORWARD'),
                value: this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.DROPDOWN_OPTIONS.STATUS.FORWARD')
            }
        ];
    }

    getCreateUserActiveScreenName(): string {
        if (this.userRegistrationStepNumber === 'step0_searchUser') {
            this.activeScreenName = this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.STEP_0');
        }
        if (this.userRegistrationStepNumber === 'step1_userRegistrationForm') {
            this.activeScreenName = this.translateService.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.STEP_1');
        }

        return this.activeScreenName;
    }

    getActiveScreenName(name: string) {
        this.activeScreenName = name;
    }

    ngOnDestroy(): void {
        this.userRegistrationService.userRegistrationsSearchForm.reset(this.userRegistrationService.defaultRegistrationsSearchFormValue);
    }

    clearAll(): void {
        this.loading = false;
        this.userRegistrationsSearchForm.reset(this.userRegistrationService.defaultRegistrationsSearchFormValue);
        this.userRegistrationsResults.clearSelectAllCheck();
        this.userRegistrationRequest = this.originaluserRegistrationRequest;
    }

    search(): void {
        this.loading = true;
        const formValue = { ...this.userRegistrationsSearchForm.value };

        if (formValue.userStatus?.length > 0 && formValue.userStatus[0]?.value !== '') {
            formValue.userStatus = formValue.userStatus[0].name;
        }
        else {
            formValue.userStatus = '';
        }

        if (formValue.companyType?.length > 0) {
            const companyTypeValue = formValue.companyType[0]?.value;
            formValue.companyType = companyTypeValue && companyTypeValue !== ''
                ? `Z${companyTypeValue}`
                : companyTypeValue;
        }

        if (formValue.country?.length > 0) {
            formValue.country = formValue.country[0].value;
        }

        this.userAPIService.userRegistrationRequest(formValue).subscribe({
            next: (requests) => {
                this.loading = false;
                if (requests?.registrations) {
                    this.userRegistrationRequest = requests.registrations;
                }
                this.userRegistrationsResults?.clearSelectAllCheck();
            },
            error: (error) => {
                this.loading = false;
                this.userAdminService.smoothScrollToTop();
                this.showToastBar('error', error.error.details);
                this.userRegistrationsResults?.clearSelectAllCheck();
            }
        });
    }

    onClickRegisterUserChangePage(event : RegisterUsers) {
        const { usersData } = event;
        this.usersData = usersData;
        this.userRegistrationStepNumber = event.registrationUserStep;
    }

    onClickRegisterUserDetailsChangePage(event : UserRegistration) {
        const { usersData } = event;
        this.userRegistrationDetails = usersData;
        this.userRegistrationStepNumber = event.registrationUserStep;
    }

    registeredUserDetails(event: UserDetails) {
        this.userDetails = event;
    }

    onContinueToStep2Click(event: string) {
        this.userRegistrationStepNumber = event;
    }

    userRegistrationDetailsSubmit(event : CreateUserDetails) {
        this.apiRequestActive = true;
        let userID: string | undefined;
        this.userAPIService.createOrRegisterUserV1(event).subscribe((response) => {
            this.apiRequestActive = false;
            this.userAdminService.smoothScrollToTop();
            this.showToastBar('success', response.details);
            this.userAdminService.userEditForm.markAsPristine();
            userID = response.details.split('UserID : ').pop();
            this.router.navigate([`/Admin/Admin-Tools/account-admin.html/user-details/${userID}`]);
        }, (error) => {
            this.apiRequestActive = false;
            this.userAdminService.smoothScrollToTop();
            this.showToastBar('error', error.error.message);
        });
    }

    private showToastBar(theme: ToastTheme, response: string) {
        this.toastService.add({
            content: response,
            theme: theme,
            id: 'createUser',
            closeable: true,
            autoClose: true
        });
    }
}
